import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { toast } from 'sonner';
import { CalendarClock, History, Printer, PenLine, FileCheck2, Layers } from 'lucide-react';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString([], { dateStyle: 'medium' }) : 'Not scheduled');

function dueState(iso) {
  if (!iso) return { label: 'No reminder', cls: 'bg-[#F1F5F9] text-[#64748B]' };
  const days = Math.ceil((new Date(iso) - new Date()) / 86400000);
  if (days < 0) return { label: `Overdue ${-days}d`, cls: 'bg-[#991B1B]/10 text-[#991B1B]' };
  if (days <= 7) return { label: `Due in ${days}d`, cls: 'bg-[#CA8A04]/15 text-[#92400E]' };
  return { label: `Due in ${days}d`, cls: 'bg-[#166534]/10 text-[#166534]' };
}

function HistoryList({ rec }) {
  return (
    <div className="max-h-64 overflow-y-auto space-y-2" data-testid={`form-record-history-${rec.id}`}>
      {(rec.history || []).map((h, i) => (
        <div key={i} className="flex items-center justify-between gap-2 text-xs border-b border-[#F1F5F9] pb-1.5">
          <div>
            <div className="font-medium text-[#0A2342] capitalize">{h.action} · {fmt(h.at)}</div>
            <div className="text-[#64748B]">{h.by_name}</div>
          </div>
          {rec.print_layout === 'pdf_original' && (
            <button className="text-[#0E7490] hover:underline" onClick={() => window.open(`/forms/${rec.form_id}/print-original?submissionId=${h.submission_id}`, '_blank')} data-testid={`history-print-${rec.id}-${i}`}>View</button>
          )}
        </div>
      ))}
    </div>
  );
}

function DueEditor({ rec, onSaved }) {
  const [due, setDue] = useState(rec.next_due_at ? rec.next_due_at.slice(0, 10) : '');
  const [interval, setInterval] = useState(rec.interval_days || '');
  const save = async () => {
    try {
      const body = {};
      if (interval && Number(interval) !== rec.interval_days) body.interval_days = Number(interval);
      if (due && due !== (rec.next_due_at || '').slice(0, 10)) body.next_due_at = due;
      if (!Object.keys(body).length) return;
      await api.patch(`/form-records/${rec.id}`, body);
      toast.success('Reminder updated');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update reminder'); }
  };
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold text-[#0A2342]">Update reminder</div>
      <label className="block text-[11px] text-[#64748B]">Next update due
        <Input type="date" value={due} onChange={e => setDue(e.target.value)} className="h-8 mt-1" data-testid={`record-due-input-${rec.id}`} />
      </label>
      <label className="block text-[11px] text-[#64748B]">Repeat every (days)
        <Input type="number" min="1" max="730" value={interval} onChange={e => setInterval(e.target.value)} className="h-8 mt-1" data-testid={`record-interval-input-${rec.id}`} />
      </label>
      <Button size="sm" className="careiq-btn-primary w-full" onClick={save} data-testid={`record-due-save-${rec.id}`}>Save</Button>
    </div>
  );
}

function RecordRow({ rec, patientId, highlighted, onChanged }) {
  const navigate = useNavigate();
  const st = dueState(rec.next_due_at);
  const isPdf = rec.print_layout === 'pdf_original';
  const fillUrl = isPdf ? `/forms/${rec.form_id}/sign-on-pdf?patientId=${patientId}` : `/forms/${rec.form_id}/fill?patientId=${patientId}`;
  return (
    <div className={`p-3 rounded-lg border transition-colors ${highlighted ? 'border-[#0E7490] bg-[#F0FDFA]' : 'border-[#E2E8F0] bg-white hover:border-[#0E7490]/40'}`} data-testid={`form-record-${rec.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-sm text-[#0A2342] truncate">{rec.form_name}</div>
          <div className="text-[11px] text-[#64748B]" data-testid={`form-record-updated-${rec.id}`}>Last updated {fmt(rec.last_updated_at)} by {rec.last_updated_by_name || '—'}</div>
          <div className="text-[11px] text-[#64748B]">Next update: <span data-testid={`form-record-due-${rec.id}`}>{fmtDate(rec.next_due_at)}</span>{rec.interval_days ? ` · every ${rec.interval_days} days` : ''}</div>
        </div>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${st.cls}`} data-testid={`form-record-status-${rec.id}`}>{st.label}</span>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate(fillUrl)} data-testid={`form-record-update-${rec.id}`}><PenLine className="w-3.5 h-3.5 mr-1" />Update</Button>
        {isPdf && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => window.open(`/forms/${rec.form_id}/print-original?submissionId=${rec.last_submission_id}`, '_blank')} data-testid={`form-record-print-${rec.id}`}><Printer className="w-3.5 h-3.5 mr-1" />View / Print</Button>}
        <Popover>
          <PopoverTrigger asChild><Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`form-record-reminder-${rec.id}`}><CalendarClock className="w-3.5 h-3.5 mr-1" />Reminder</Button></PopoverTrigger>
          <PopoverContent className="w-64"><DueEditor rec={rec} onSaved={onChanged} /></PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild><Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`form-record-history-btn-${rec.id}`}><History className="w-3.5 h-3.5 mr-1" />History ({(rec.history || []).length})</Button></PopoverTrigger>
          <PopoverContent className="w-80"><HistoryList rec={rec} /></PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

export default function PatientFormRecords({ patientId }) {
  const [records, setRecords] = useState([]);
  const [params] = useSearchParams();
  const focus = params.get('formRecord');
  const navigate = useNavigate();
  const load = useCallback(() => {
    api.get(`/patients/${patientId}/form-records`).then(r => setRecords(r.data.records || [])).catch(() => {});
  }, [patientId]);
  useEffect(() => { load(); }, [load]);
  if (!records.length) return null;
  return (
    <Card className="careiq-card print:hidden" data-testid="patient-form-records">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2"><FileCheck2 className="w-4 h-4 text-[#0E7490]" />Form Records &amp; Update Schedule</CardTitle>
          {records.some(r => r.print_layout === 'pdf_original') && (
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => navigate(`/patients/${patientId}/recert-packet`)} data-testid="recert-packet-btn"><Layers className="w-3.5 h-3.5 mr-1" />Print recert packet</Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="grid md:grid-cols-2 gap-3">
        {records.map(r => <RecordRow key={r.id} rec={r} patientId={patientId} highlighted={focus === r.id} onChanged={load} />)}
      </CardContent>
    </Card>
  );
}
